import React, { useState } from 'react';
import {
  Typography,
  Container,
  Box,
  Button,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  People as PeopleIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { BottomNavigation } from '../layout/BottomNavigation';
import { TopBar } from '../layout/TopBar';
import { useNavigate } from 'react-router-dom';
import { useSocial } from '../hooks/useSocial';
import { PostCard } from '../components/social/PostCard';
import { CreatePostDialog } from '../components/social/CreatePostDialog';

export const HostlyClubPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const {
    posts,
    isLoading,
    error,
    feedMode,
    setFeedMode,
    createNewPost,
    toggleLike,
    checkLike,
    toggleFavorite,
    checkFavorite,
    addPostComment,
    getComments,
    removePost,
  } = useSocial();

  // Require authentication
  if (!authLoading && !user) {
    navigate('/login');
    return null;
  }

  const handleCreatePost = async (content: string, imageUrl?: string) => {
    await createNewPost(content, imageUrl, imageUrl ? 'image' : 'text');
  };

  return (
    <>
      <TopBar
        title="HostlyClub"
        icon={<PeopleIcon sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        showSearchButton={true}
        onBackClick={() => navigate(-1)}
        onSearchClick={() => navigate('/hostlyclub/discover')}
      />

      <Box
        sx={{
          backgroundColor: theme.colors.background,
          minHeight: '100vh',
          paddingBottom: '120px',
          paddingTop: '70px',
        }}
      >
        <Container maxWidth="sm" sx={{ padding: 0 }}>
          {/* Botão rosinha em cima - Adicionar Foto */}
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Button
              startIcon={<AddIcon />}
              fullWidth
              onClick={() => setCreateDialogOpen(true)}
              sx={{
                backgroundColor: theme.colors.secondary,
                color: theme.colors.text.onSecondary,
                borderRadius: theme.radii.medium,
                padding: '12px',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: theme.shadows.small,
                '&:hover': { backgroundColor: theme.colors.secondary, opacity: 0.9 },
              }}
            >
              Adicionar Foto
            </Button>
          </Box>

          {/* Search Bar */}
          <Box sx={{ px: 2, pb: 2 }}>
            <Box
              onClick={() => navigate('/hostlyclub/discover')}
              sx={{
                backgroundColor: theme.colors.card,
                padding: '12px 16px',
                borderRadius: theme.radii.pill,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                boxShadow: theme.shadows.small,
                cursor: 'pointer',
                border: `1px solid ${theme.colors.border}`,
              }}
            >
              <SearchIcon sx={{ color: theme.colors.text.secondary }} />
              <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
                BUSCAR USUÁRIOS
              </Typography>
            </Box>
          </Box>
          {/* Tabs para Feed / Explorar */}
          <Tabs
            value={feedMode === 'personalized' ? 0 : 1}
            onChange={(_, newValue) => setFeedMode(newValue === 0 ? 'personalized' : 'explore')}
            variant="fullWidth"
            sx={{
              backgroundColor: theme.colors.card,
              borderBottom: `1px solid ${theme.colors.border}`,
              '& .MuiTab-root': {
                color: theme.colors.text.secondary,
                textTransform: 'none',
                fontWeight: 'bold',
                '&.Mui-selected': {
                  color: theme.colors.primary,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.colors.primary,
              },
            }}
          >
            <Tab label="Feed" />
            <Tab label="Explorar" />
          </Tabs>

          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: theme.colors.primary }} />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Box sx={{ p: 2 }}>
              <Typography sx={{ color: theme.colors.text.primary }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {!isLoading && !error && posts.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                px: 2,
              }}
            >
              <PeopleIcon sx={{ fontSize: 80, color: theme.colors.text.secondary, mb: 2 }} />
              <Typography
                variant="h6"
                sx={{ color: theme.colors.text.primary, mb: 1, textAlign: 'center' }}
              >
                Nenhum post ainda
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.colors.text.secondary, textAlign: 'center' }}
              >
                Seja o primeiro a compartilhar algo no HostlyClub!
              </Typography>
            </Box>
          )}

          {/* Posts Feed */}
          {!isLoading && posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onLike={toggleLike}
              onFavorite={toggleFavorite}
              onComment={addPostComment}
              onDelete={post.userId === user?.uid ? removePost : undefined}
              checkLike={checkLike}
              checkFavorite={checkFavorite}
              getComments={getComments}
            />
          ))}
        </Container>

        {/* Create Post Dialog */}
        <CreatePostDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSubmit={handleCreatePost}
        />

        {/* Bottom Navigation */}
        <BottomNavigation />
      </Box>
    </>
  );
};


